import { useEffect, useRef } from 'react';

const credentials = [
  { label: 'Inscrição OAB/SP', value: 'Regularmente inscrito' },
  { label: 'Especialização', value: 'Direito das Sucessões e Direito Imobiliário' },
  { label: 'Experiência', value: 'Atuação focada em Direito Imobiliário' },
  { label: 'Região de Atuação', value: 'Grande ABC Paulista e São Paulo' },
  { label: 'OAB', value: '526.353' },
];

const pillars = [
  'Inventário Judicial e Extrajudicial',
  'Partilha e Transmissão de Bens',
  'Regularização Imobiliária',
  'Direito das Sucessões',
];

function useScrollReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(24px)';
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            if (!el) return;
            el.style.transition = 'opacity 0.85s ease, transform 0.85s ease';
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
          }, delay);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [delay]);
  return ref;
}

export default function About() {
  const leftRef = useScrollReveal(0);
  const rightRef = useScrollReveal(150);

  return (
    <section id="sobre" className="bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[2fr_5fr]">

          {/* Left — Dark panel with photo */}
          <div ref={leftRef} className="lg:col-span-1 relative bg-[#0C3A22] min-h-[620px]">
            <img
              src="https://storage.readdy-site.link/project_files/66d90ef2-528e-4451-823d-a1eab56ea0b6/550ea940-4309-42b5-9a81-00ba56cdf767_DSC_6303.jpg?v=18e566ad69130fcf6faf5ada61ee4538"
              alt="Dr. Bruno Castro — Advogado especialista em inventário e direito imobiliário"
              className="w-full h-full object-cover object-center absolute inset-0"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0C3A22] via-[#0C3A22]/30 to-transparent"></div>

            {/* Bottom name plate */}
            <div className="absolute bottom-0 left-0 right-0 p-8 z-10">
              <div className="border-l-2 border-[#C7A76C] pl-5">
                <p className="font-serif text-white text-2xl font-semibold tracking-wide">
                  Dr. Bruno Castro
                </p>
                <p className="font-sans text-[#D6BA85] text-sm mt-1.5 leading-snug">
                  Advogado — OAB/SP<br />
                  Especialista em Inventário e Direito Imobiliário
                </p>
              </div>
            </div>
          </div>

          {/* Right — Content */}
          <div ref={rightRef} className="lg:col-span-1 flex flex-col justify-center px-10 lg:px-16 py-20">

            {/* Section label */}
            <div className="flex items-center gap-3 mb-10">
              <div className="w-6 h-px bg-[#C7A76C]"></div>
              <span className="font-sans text-[#C7A76C] text-xs font-medium tracking-widest uppercase">
                Sobre o Advogado
              </span>
            </div>

            <h2 className="font-serif text-3xl lg:text-4xl text-[#0C3A22] font-semibold leading-snug mb-6">
              Rigor técnico e comprometimento<br />
              com o seu patrimônio
            </h2>

            <div className="w-10 h-px bg-[#C7A76C] mb-8"></div>

            <p className="font-sans text-[#3A3A3A] text-[15px] leading-relaxed mb-5">
              O Dr. <strong>Bruno Castro</strong> é advogado com dedicação exclusiva ao Direito
              das Sucessões e ao Direito Imobiliário. Sua atuação é fundamentada na análise
              criteriosa de cada caso, na condução técnica dos processos e na comunicação
              direta e honesta com cada cliente.
            </p>

            <p className="font-sans text-[#3A3A3A] text-[15px] leading-relaxed mb-10">
              Referência no Grande ABC Paulista, o escritório atende famílias que precisam
              de segurança jurídica em situações de <strong>inventário, herança e
              regularização de imóveis</strong>, com comprometimento total em cada etapa
              do processo.
            </p>

            {/* Areas of expertise */}
            <div className="grid grid-cols-2 gap-2 mb-10">
              {pillars.map((p) => (
                <div key={p} className="flex items-center gap-2.5">
                  <div className="w-1 h-1 rounded-full bg-[#C7A76C] flex-shrink-0"></div>
                  <span className="font-sans text-[#3A3A3A] text-sm">{p}</span>
                </div>
              ))}
            </div>

            {/* Credentials table */}
            <div className="border-t border-[#E8E4DC]">
              {credentials.map((item, i) => (
                <div
                  key={item.label}
                  className={`grid grid-cols-5 py-3.5 ${
                    i < credentials.length - 1 ? 'border-b border-[#E8E4DC]' : ''
                  }`}
                >
                  <span className="col-span-2 font-sans text-xs text-[#3A3A3A]/50 uppercase tracking-wide self-center">
                    {item.label}
                  </span>
                  <span className="col-span-3 font-sans text-sm text-[#121212] font-medium">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-10 flex justify-center lg:justify-start">
              <a
                href="https://wa.me/5511958814422?text=Olá%2C%20Dr.%20Bruno%2C%20gostaria%20de%20agendar%20uma%20consulta"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2.5 bg-[#0F4A2B] hover:bg-[#0C3A22] text-white font-sans font-semibold text-sm px-8 py-4 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line text-[#C7A76C] text-base"></i>
                Agendar uma consulta
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
